import { Tooltip } from "antd";
import {
    ReactNode,
    useEffect,
    useRef,
    useState,
} from "react";
import { datetime } from "../../../util/library/datetime";
import { SHORT_DATETIME_FORMAT } from "../../../util/varibles/constants";
import { useProcessData } from "./ProcessDataContext";

export interface ITimePoint {
    name: string | ReactNode;
    key?: string
    start: number;
    finish?: number;
    steps?: Record<
        string,
        {
            name: string;
            start: number;
            finish: number;
        }
    >;
}

type TAlignment = "left" | "center" | "right";

export default function TimePoint({
    data,
    onClick,
    row = 0,
    index,
}: {
    data: ITimePoint;
    onClick?: () => void;
    row?: number;
    index: number;
}) {
    const [startTime] = useProcessData(state => state.start);
    const [finishTime] = useProcessData(state => state.finish);

    const labelRef = useRef<HTMLDivElement>(null);
    const [alignment, setAlignment] = useState<TAlignment>("center");

    const duration = finishTime - startTime;

    const left =
        duration > 0
            ? ((data.start - startTime) / duration) * 100
            : 0;

    const width =
        data.finish && duration > 0
            ? ((data.finish - data.start) / duration) * 100
            : undefined;

    useEffect(() => {
        const labelElement = labelRef.current;
        const containerElement = labelElement?.parentElement?.parentElement;

        if (!labelElement || !containerElement) {
            return;
        }

        const updateAlignment = () => {
            const containerWidth = containerElement.clientWidth;
            const labelWidth = labelElement.scrollWidth;

            const startPosition = (left / 100) * containerWidth;
            const rangeWidth = width
                ? (width / 100) * containerWidth
                : 0;

            // Với một range, label được đặt ở giữa range.
            // Với một time point, label được đặt ngay tại startPosition.
            const labelCenter = data.finish
                ? startPosition + rangeWidth / 2
                : startPosition;

            const labelLeft = labelCenter - labelWidth / 2;
            const labelRight = labelCenter + labelWidth / 2;

            if (labelLeft < 0) {
                setAlignment("left");
                return;
            }

            if (labelRight > containerWidth) {
                setAlignment("right");
                return;
            }

            setAlignment("center");
        };

        updateAlignment();

        const resizeObserver = new ResizeObserver(updateAlignment);
        resizeObserver.observe(containerElement);
        resizeObserver.observe(labelElement);

        return () => {
            resizeObserver.disconnect();
        };
    }, [left, width, data.finish, data.name]);

    const positionClass = data.finish
        ? {
            left: "text-left",
            center: "text-center",
            right: "text-right",
        }[alignment]
        : {
            left: "translate-x-0",
            center: "-translate-x-1/2",
            right: "-translate-x-full",
        }[alignment];

    return (
        <div
            className={`absolute ${positionClass}`}
            style={{
                left: `${left}%`,
                top: `${row * 24}px`,
                width: width ? `${width}%` : undefined,
            }}
        >
            <Tooltip
                title={
                    data.finish
                        ? `${datetime(data.start).format(
                            SHORT_DATETIME_FORMAT,
                        )} - ${datetime(data.finish).format(
                            SHORT_DATETIME_FORMAT,
                        )}`
                        : datetime(data.start).format(
                            SHORT_DATETIME_FORMAT,
                        )
                }
            >
                <div
                    ref={labelRef}
                    data-time-point-label
                    data-time-point-index={index}
                    className={[
                        "relative z-10",
                        "inline-block whitespace-nowrap",
                        onClick ? "cursor-pointer" : "cursor-default",
                        data.finish
                            ? "transition-all duration-300 hover:text-[--colorLink]"
                            : "",
                    ].join(" ")}
                    onClick={onClick}
                >
                    {data.name}
                </div>
            </Tooltip>
        </div>
    );
}
